import { useEffect } from 'react';
import { useSubNavContext, type SubNavConfig } from '../contexts/SubNavContext';

/**
 * Hook to register a sub-navigation configuration.
 * When the component using this hook mounts, the sub-nav becomes active.
 * When it unmounts, the sub-nav is cleared.
 *
 * The effect re-runs whenever the `config` reference changes, so switching
 * between sections that share the same item count (e.g. Suites ↔ Labs within a
 * single still-mounted layout) correctly swaps the sub-nav. Callers MUST pass a
 * stable/memoized config object (via `useMemo` or a module-level constant) so a
 * fresh object isn't produced on every render — every current caller does.
 *
 * @param config - The sub-navigation configuration to register (must be stable)
 */
export function useSubNav(config: SubNavConfig): void {
  const { setConfig } = useSubNavContext();

  useEffect(() => {
    setConfig(config);

    return () => {
      setConfig(null);
    };
  }, [setConfig, config]);
}
